import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '@/hooks/useUserRole';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Shield, Edit, Eye, User, Calendar, Settings } from 'lucide-react';
import { AppSidebar } from '@/components/AppSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';

const AVAILABLE_MODELS = [
  { id: 'openai/gpt-4o', name: 'GPT-4o' },
  { id: 'openai/gpt-4o-mini', name: 'GPT-4o Mini' },
  { id: 'openai/gpt-4o-nano', name: 'GPT-4o Nano' },
  { id: 'google/gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
  { id: 'google/gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
  { id: 'google/gemini-2.5-flash-lite', name: 'Gemini 2.5 Flash Lite' },
  { id: 'anthropic/claude-3.5-sonnet', name: 'Claude 3.5 Sonnet' },
  { id: 'anthropic/claude-3.5-haiku', name: 'Claude 3.5 Haiku' },
  { id: 'meta-llama/llama-3.3-70b-instruct', name: 'Llama 3.3 70B' },
  { id: 'meta-llama/llama-3.1-8b-instruct', name: 'Llama 3.1 8B' },
  { id: 'deepseek/deepseek-chat', name: 'DeepSeek Chat' },
  { id: 'google/gemini-flash-thinking-exp-1219', name: 'Gemini Flash Thinking' },
];

interface LLMConfig {
  role: 'admin' | 'editor' | 'cliente' | 'guest' | 'rsvp';
  default_model: string;
}

const LLMConfig = () => {
  const [configs, setConfigs] = useState<LLMConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const { role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!roleLoading && role !== 'admin') {
      toast({
        title: 'Acesso negado',
        description: 'Apenas administradores podem acessar esta página.',
        variant: 'destructive',
      });
      navigate('/');
    }
  }, [role, roleLoading, navigate, toast]);

  useEffect(() => {
    if (role === 'admin') {
      fetchConfigs();
    }
  }, [role]);

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('user_role_llm_config')
        .select('role, default_model')
        .order('role');

      if (error) throw error;

      setConfigs(data || []);
    } catch (error: any) {
      console.error('Error loading LLM configs:', error);
      toast({
        title: 'Erro ao carregar configurações',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModelChange = async (roleValue: 'admin' | 'editor' | 'cliente' | 'guest' | 'rsvp', newModel: string) => {
    try {
      const { error } = await supabase
        .from('user_role_llm_config')
        .update({ default_model: newModel })
        .eq('role', roleValue);

      if (error) throw error;

      toast({
        title: 'Modelo atualizado',
        description: `Modelo padrão alterado para ${AVAILABLE_MODELS.find(m => m.id === newModel)?.name || newModel}`,
      });

      fetchConfigs();
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar modelo',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const getRoleBadgeVariant = (roleValue: string) => {
    switch (roleValue) {
      case 'admin':
        return 'default';
      case 'editor':
      case 'cliente':
      case 'rsvp':
        return 'secondary';
      case 'guest':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getRoleIcon = (roleValue: string) => {
    switch (roleValue) {
      case 'admin':
        return <Shield className="h-4 w-4" />;
      case 'editor':
        return <Edit className="h-4 w-4" />;
      case 'cliente':
        return <User className="h-4 w-4" />;
      case 'guest':
        return <Eye className="h-4 w-4" />;
      case 'rsvp':
        return <Calendar className="h-4 w-4" />;
      default:
        return null;
    }
  };

  const getRoleLabel = (roleValue: string) => {
    switch (roleValue) {
      case 'admin':
        return 'Administrador';
      case 'editor':
        return 'Editor';
      case 'cliente':
        return 'Cliente';
      case 'guest':
        return 'Convidado';
      case 'rsvp':
        return 'RSVP';
      default:
        return roleValue;
    }
  };

  if (roleLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar onClearData={() => {}} />
        <main className="flex-1 overflow-auto p-4 md:p-8">
          <div className="max-w-4xl mx-auto space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  <div>
                    <CardTitle>Configurações de Modelos LLM</CardTitle>
                    <CardDescription>
                      Defina o modelo padrão para cada categoria de usuário no Assistente de IA
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Categoria</TableHead>
                      <TableHead>Modelo Padrão</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {configs.map((config) => (
                      <TableRow key={config.role}>
                        <TableCell>
                          <Badge variant={getRoleBadgeVariant(config.role)} className="gap-1">
                            {getRoleIcon(config.role)}
                            {getRoleLabel(config.role)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={config.default_model}
                            onValueChange={(value) => handleModelChange(config.role, value)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {AVAILABLE_MODELS.map((model) => (
                                <SelectItem key={model.id} value={model.id}>
                                  {model.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default LLMConfig;
